import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { num } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, Check, X } from "lucide-react";

export default function InspectionLotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [chars, setChars] = useState([]);

  const load = () => api.get(`/inspection-lots/${id}`).then((r) => { setLot(r.data); setChars((r.data.characteristics || []).map((c) => ({ ...c }))); }).catch(() => setLot(false));
  useEffect(() => { load(); }, [id]);

  if (lot === null) return <Loader label="Loading inspection lot…" />;
  if (lot === false) return <div className="p-8 text-[#6a6d70]">Inspection lot not found.</div>;

  const setVal = (i, v) => { const c = [...chars]; c[i] = { ...c[i], value: v }; setChars(c); };
  const valuate = (c) => c.value === "" || c.value === null || c.value === undefined ? "OPEN" : (Number(c.value) >= c.spec_min && Number(c.value) <= c.spec_max ? "PASS" : "FAIL");

  const record = async () => {
    const payload = chars.filter((c) => c.value !== "" && c.value !== null && c.value !== undefined).map((c) => ({ name: c.name, value: Number(c.value) }));
    if (payload.length === 0) { toast.error("Enter at least one measured value"); return; }
    try {
      const { data } = await api.post(`/inspection-lots/${id}/record-results`, { characteristics: payload });
      toast.success(`Results recorded · valuation ${data.result}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Recording failed"); }
  };

  const decide = async (decision) => {
    try {
      const { data } = await api.post(`/inspection-lots/${id}/usage-decision`, { decision });
      toast.success(`Usage decision: ${decision} (${data.result})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Usage decision failed"); }
  };

  const inp = "rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-sm outline-none focus:border-[#EAB308] w-32";
  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/quality")} data-testid="lot-back" className="inline-flex items-center gap-1.5 text-sm text-[#0a6ed1] hover:underline"><ArrowLeft size={16} /> Quality Management</button>
      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3"><h1 className="text-2xl font-semibold tracking-tight text-[#32363a]">Lot {lot.lot_no}</h1><StatusBadge status={lot.result} /></div>
            <p className="mt-1 text-sm text-[#6a6d70]">Results Recording · QE01 · {lot.material_name}</p>
          </div>
          <div className="flex items-center gap-3">
            {lot.usage_decision && <div className="text-right"><div className="text-[11px] uppercase text-[#6a6d70]">Usage Decision</div><div className="text-lg font-semibold text-[#32363a]">{lot.usage_decision}</div></div>}
            <button data-testid="lot-record-btn" onClick={record} className="inline-flex items-center gap-1.5 rounded-md bg-[#EAB308] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ca9a04]"><ClipboardCheck size={16} /> Record Results</button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-[#eff1f4] pt-4 sm:grid-cols-4 text-sm">
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Material</div><div className="font-mono text-xs">{lot.material_code}</div></div>
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Lot Qty</div><div className="font-mono">{num(lot.qty)}</div></div>
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Inspector</div><div>{lot.inspector}</div></div>
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Recorded</div><div>{lot.recorded ? "Yes" : "No"}</div></div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm" data-testid="lot-chars-table">
        <div className="border-b border-[#eff1f4] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Inspection Characteristics</div>
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-[#e5e9f0] bg-[#f7f8fa] text-[11px] uppercase tracking-wide text-[#6a6d70]"><th className="px-3 py-2.5">Characteristic</th><th className="px-3 py-2.5">Spec (min–max)</th><th className="px-3 py-2.5">Measured</th><th className="px-3 py-2.5">Valuation</th></tr></thead>
          <tbody>
            {chars.map((c, i) => {
              const v = valuate(c);
              return (
                <tr key={i} className="border-b border-[#eff1f4] last:border-0">
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#6a6d70]">{c.spec_min} – {c.spec_max}</td>
                  <td className="px-3 py-2"><input data-testid={`lot-char-value-${i}`} type="number" step="0.01" value={c.value ?? ""} onChange={(e) => setVal(i, e.target.value)} className={inp} /></td>
                  <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-[11px] font-medium ${v === "PASS" ? "bg-emerald-100 text-emerald-700" : v === "FAIL" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{v}</span></td>
                </tr>);
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm">
        <div className="text-sm font-semibold text-[#32363a]">Usage Decision</div>
        <button data-testid="lot-accept-btn" onClick={() => decide("ACCEPT")} disabled={!lot.recorded} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-sm text-emerald-600 hover:border-emerald-500 disabled:opacity-40"><Check size={15} /> Accept</button>
        <button data-testid="lot-reject-btn" onClick={() => decide("REJECT")} disabled={!lot.recorded} className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm text-red-600 hover:border-red-500 disabled:opacity-40"><X size={15} /> Reject</button>
        {!lot.recorded && <span className="text-xs text-[#6a6d70]">Record results before making a usage decision.</span>}
      </div>
    </div>
  );
}
